import { Form, Select } from 'antd';
import React, { memo, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import DefaultFormItem from 'components/composites/DefaultFormItem';
import FormItemCol from 'components/forms/FormItemCol';
import { loadBackOfficeEnterpriseSites } from 'store/modules/backOfficeEnterpriseSites/actions';
import useBackOfficeEnterpriseSites from 'store/modules/backOfficeEnterpriseSites/hooks';

type Props = {
  filterByEnterprise?: boolean;
  label?: string;
  placeholder?: string;
  selectId?: string;
};

function BackOfficeSiteSelect({
  filterByEnterprise,
  label = 'Site',
  placeholder = 'Site',
  selectId
}: Props): JSX.Element {
  const dispatch = useDispatch();
  const form = Form.useFormInstance();
  const enterpriseField = Form.useWatch('enterpriseId', form);
  const enterpriseId = Number(enterpriseField);
  const hasEnterprise = Number.isInteger(enterpriseId) && enterpriseId > 0;
  const sites = useBackOfficeEnterpriseSites();

  const siteOptions = useMemo(
    () =>
      sites
        .filter((site) => !filterByEnterprise || site.enterpriseId === enterpriseId)
        .map((site) => ({
          label: site.name,
          value: site.id
        })),
    [sites, enterpriseId, filterByEnterprise]
  );

  useEffect(() => {
    if (hasEnterprise) {
      dispatch(loadBackOfficeEnterpriseSites(enterpriseId));
    }
  }, [hasEnterprise, enterpriseId, dispatch]);

  return (
    <FormItemCol>
      <DefaultFormItem
        name="siteId"
        label={label}
        rules={[{ required: true, message: 'Please select a site' }]}
      >
        <Select
          id={selectId}
          placeholder={placeholder}
          options={siteOptions}
          disabled={!hasEnterprise}
        />
      </DefaultFormItem>
    </FormItemCol>
  );
}

export default memo(BackOfficeSiteSelect);
